import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { TextAreaField } from "@/components/ui/FormFields";
import { useAuth } from "@/context/AuthContext";
import {
  getVoice,
  standWithVoice,
  removeStand,
  getVoiceComments,
  createVoiceComment,
  upvoteVoiceComment,
  deleteVoiceComment,
  type VoiceComment,
} from "@/api/voices";
import VoiceLanes from "./Lanes";

export default function VoiceDetail() {
  const { idOrSlug } = useParams<{ idOrSlug: string }>();
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["voice", idOrSlug],
    queryFn: () => getVoice(idOrSlug!),
    enabled: !!idOrSlug,
  });
  const v = data?.data.voice;

  const { data: commentsData } = useQuery({
    queryKey: ["voice-comments", v?.id],
    queryFn: () => getVoiceComments(v!.id),
    enabled: !!v?.id,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["voice", idOrSlug] });
    qc.invalidateQueries({ queryKey: ["voice-comments", v?.id] });
  }

  async function toggleStand() {
    if (!v) return;
    setBusy(true);
    setFeedback(null);
    try {
      if (v.hasStood) {
        await removeStand(v.id);
      } else {
        await standWithVoice(v.id);
      }
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  // Shared by the comment actions. Both are refused for anyone but the author
  // (and upvoting your own is refused too) — the errors are the feature, so
  // show the server's own words rather than a generic failure.
  async function act(fn: () => Promise<unknown>) {
    setBusy(true);
    setFeedback(null);
    try {
      await fn();
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  }

  async function postComment() {
    if (!v || comment.trim().length < 1) return;
    setBusy(true);
    setFeedback(null);
    try {
      await createVoiceComment(v.id, comment.trim());
      setComment("");
      invalidate();
    } catch (e) {
      setFeedback(e instanceof Error ? e.message : "Failed to comment");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (error)
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
        Failed to load voice.{" "}
        <button onClick={() => refetch()} className="underline font-medium">
          Retry
        </button>
      </div>
    );
  if (!v) return <p className="text-sm text-gray-500">Not found.</p>;

  const comments = commentsData?.data.comments ?? [];
  const topSlot = commentsData?.data.topSlot ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={v.title}
        subtitle={`by ${v.user?.name ?? "Anonymous"} · ${v.standCount} stands · ${v.commentCount} comments`}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Voices", href: "/lab/voices" },
          { label: v.title.length > 40 ? v.title.slice(0, 40) + "…" : v.title },
        ]}
        actions={
          isAuthenticated && (
            <Button
              size="sm"
              variant={v.hasStood ? "primary" : "secondary"}
              onClick={toggleStand}
              isLoading={busy}
            >
              {v.hasStood ? "Standing ✓" : "Stand with this"}
            </Button>
          )
        }
      />

      {feedback && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-3 py-2 rounded">
          {feedback}
        </div>
      )}

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <Badge variant="gray">{v.voiceType}</Badge>
          <Badge variant="blue">{v.category.replace(/_/g, " ")}</Badge>
          <Badge variant="purple">{v.intentType.replace(/_/g, " ")}</Badge>
          {v.status && <Badge variant="gray">{v.status}</Badge>}
        </div>
        {v.description && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {v.description}
          </p>
        )}

        {/* The carousel, weighed and labelled. `reduced` renders smaller and
            quieter — IN PLACE, fully readable: that binding is the client's
            half of the presentation-weight contract, demonstrated here. */}
        {v.media && v.media.length > 0 && (
          <div className="mt-3 flex gap-3 overflow-x-auto pb-1">
            {v.media.map((m) => (
              <figure
                key={m.id}
                className={
                  m.presentationWeight === "reduced"
                    ? "w-36 shrink-0 opacity-80"
                    : "w-56 shrink-0"
                }
              >
                {m.mediaType === "video" ? (
                  <video
                    src={m.url}
                    poster={m.thumbnailUrl ?? undefined}
                    controls
                    className="w-full rounded-lg border border-gray-200"
                  />
                ) : (
                  <img
                    src={m.url}
                    alt={m.altText ?? ""}
                    className="w-full rounded-lg border border-gray-200 object-cover"
                  />
                )}
                <figcaption className="mt-1 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
                  {m.presentationWeight === "marked" && (
                    <span className="rounded bg-violet-100 px-1.5 py-0.5 font-medium text-violet-700">
                      AI
                    </span>
                  )}
                  {m.c2paPresent && (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700">
                      credentials at upload
                    </span>
                  )}
                  <span>{m.provenanceLabel}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}
        {Object.keys(v.stanceCounts).length > 0 && (
          <div className="flex gap-2 flex-wrap mt-3">
            {Object.entries(v.stanceCounts).map(([stance, count]) => (
              <Badge key={stance} variant="green">
                {stance}: {count}
              </Badge>
            ))}
          </div>
        )}
      </section>

      <VoiceLanes voiceId={v.id} />

      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {v.commentCount} {v.commentCount === 1 ? "Comment" : "Comments"}
        </h2>

        {isAuthenticated && (
          <div className="mb-4">
            <TextAreaField
              label=""
              rows={3}
              placeholder="Add a comment…"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <div className="mt-2">
              <Button
                size="sm"
                onClick={postComment}
                isLoading={busy}
                disabled={comment.trim().length < 1}
              >
                Comment
              </Button>
            </div>
          </div>
        )}

        {/* The two-mode lead on a contested thread. It only ADDS: every item
            here also appears in the chronological list below, in place. The
            label renders VERBATIM — a client must never invent an agreement
            framing ("both sides agreed"), which is forbidden until the
            offline bridging work is validated. */}
        {topSlot && (
          <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-amber-900">{topSlot.label}</span>
              <Badge variant="yellow">{topSlot.mode}</Badge>
            </div>
            <ul className="mt-2 space-y-2">
              {topSlot.items.map((t) => (
                <li key={t.id} className="text-sm text-gray-800">
                  <span className="font-medium">{t.user?.name ?? "Anonymous"}</span>
                  {(t.standing ?? []).map((s) => (
                    <span
                      key={s.label}
                      className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-[11px] text-green-800"
                    >
                      {s.label}
                    </span>
                  ))}
                  {t.sourceUrl && (
                    <a
                      href={t.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-[11px] font-medium text-blue-700 hover:underline"
                    >
                      source ↗
                    </a>
                  )}
                  <p className="mt-0.5 text-gray-700">{t.content}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {comments.length === 0 ? (
          <p className="text-sm text-gray-500">No comments yet.</p>
        ) : (
          <ul className="space-y-3">
            {comments.map((c) => (
              <CommentRow
                key={c.id}
                c={c}
                signedIn={isAuthenticated}
                busy={busy}
                onUpvote={() => act(() => upvoteVoiceComment(c.id))}
                onDelete={() => act(() => deleteVoiceComment(c.id))}
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CommentRow({
  c,
  signedIn,
  busy,
  onUpvote,
  onDelete,
}: {
  c: VoiceComment;
  signedIn: boolean;
  busy: boolean;
  onUpvote: () => void;
  onDelete: () => void;
}) {
  return (
    <li className="border border-gray-200 rounded-xl px-4 py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-gray-900">{c.user?.name ?? "Anonymous"}</span>
        {/* Standing — WHO IS CLOSE to the subject, as a label and never a
            score. Empty on a civic post, and that silence is the honest
            answer rather than a gap to fill. */}
        {(c.standing ?? []).map((s) => (
          <Badge key={s.label} variant="green">
            {s.label}
          </Badge>
        ))}
        {/* Where the reply stands. Exists only on a contested post. */}
        {c.position && <Badge variant="purple">{c.position.replace("_", " ")}</Badge>}
        <span className="text-xs text-gray-400">
          {new Date(c.createdAt).toLocaleDateString()}
        </span>
        {c.upvoteCount > 0 && (
          <Badge variant="gray">▲ {c.upvoteCount}</Badge>
        )}
      </div>
      <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
        {c.content}
      </p>
      {c.sourceUrl && (
        <a
          href={c.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline"
        >
          source ↗
        </a>
      )}
      {c.replyCount > 0 && (
        <div className="text-xs text-gray-400 mt-1">
          {c.replyCount} {c.replyCount === 1 ? "reply" : "replies"}
        </div>
      )}
      {signedIn && (
        <div className="flex gap-3 mt-2">
          <button
            disabled={busy}
            onClick={onUpvote}
            className="text-xs text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            ▲ upvote
          </button>
          <button
            disabled={busy}
            onClick={onDelete}
            className="text-xs text-red-700 disabled:opacity-50"
          >
            delete
          </button>
        </div>
      )}
    </li>
  );
}
